import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Upload, Link as LinkIcon, Eye, Trash2, FileText, Copy, Check, X, Plus, Mail, ChevronDown, ChevronUp, Clock, Archive, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Card, Btn, Input, Label, IconBtn, StatusBadge, Spinner } from "./ui-kit";
import PdfProofViewer from "./PdfProofViewer";
import RecipientMultiSelectPopover from "./RecipientMultiSelectPopover";
import api, { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatDateUK } from "@/lib/format";


const ProjectProofs = ({ projectId, projectName, clientId, clientName }) => {
  const [proofs, setProofs] = useState([]);
  const [loading, setLoading] = useState(true);
  // Default-closed: completed proofs are tucked away to keep the active list
  // focused. Persists in memory only — re-collapses on page navigation.
  const [completedOpen, setCompletedOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [file, setFile] = useState(null);
  const [activeProof, setActiveProof] = useState(null);
  const [activeFileBlobUrl, setActiveFileBlobUrl] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [versionFor, setVersionFor] = useState(null);
  const versionFileRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const autoOpenedRef = useRef(false);
  const fromParam = searchParams.get("from"); // "proofing" if opened from Proofing page
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [contactEmails, setContactEmails] = useState([]); // [{name, email}]
  const { user } = useAuth();

  // Pull this project's client once so we can offer "Email link" with
  // pre-filled recipient(s) from the client's contacts.
  useEffect(() => {
    if (!clientId) { setContactEmails([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/clients/${clientId}`);
        if (cancelled) return;
        const out = [];
        (data.contacts || []).forEach((c) => {
          const e = (c.email || "").trim();
          if (e) out.push({ name: c.name || e, email: e });
        });
        const ce = (data.email || "").trim();
        if (ce && !out.some((x) => x.email.toLowerCase() === ce.toLowerCase())) {
          out.unshift({ name: data.name || ce, email: ce });
        }
        setContactEmails(out);
      } catch {
        setContactEmails([]);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get(`/projects/${projectId}/proofs`);
    setProofs(data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  // Auto-open a specific proof if URL says so. Accepts either:
  //   ?proof=latest   → opens the newest proof in this project (e.g. from project list)
  //   ?proof=<id>     → opens that specific proof (e.g. clicked from the global Proofing page)
  useEffect(() => {
    if (autoOpenedRef.current) return;
    const target = searchParams.get("proof");
    if (!target) return;
    if (proofs.length === 0) return;
    autoOpenedRef.current = true;
    let toOpen = null;
    if (target === "latest") {
      toOpen = proofs.slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
    } else {
      toOpen = proofs.find((x) => x.id === target);
    }
    if (toOpen) {
      openViewer(toOpen);
      // remove the param so it doesn't re-trigger
      const next = new URLSearchParams(searchParams);
      next.delete("proof");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [proofs, searchParams]);

  const submitNew = async (e) => {
    e.preventDefault();
    if (!file || !name.trim()) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("name", name.trim());
    fd.append("file", file);
    try {
      await api.post(`/projects/${projectId}/proofs`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      setShowUploadForm(false);
      setName("");
      setNameTouched(false);
      setFile(null);
    } finally {
      setUploading(false);
    }
  };

  const onVersionFile = async (proofId, e) => {
    const f = e.target.files[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      await api.post(`/proofs/${proofId}/new-version`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } finally {
      setUploading(false);
      e.target.value = "";
      setVersionFor(null);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/proofs/${id}`);
    } catch (err) {
      alert(`Could not delete proof: ${err?.response?.data?.detail || err.message || "Unknown error"}`);
      return;
    }
    setConfirmDeleteId(null);
    await load();
    if (activeProof?.id === id) closeViewer();
  };

  // Build a human-readable slug for the proof URL.
  // Used as a cosmetic suffix to the share token so links read like:
  //   /proof/{token}/8050-bowood-hotel-golf-membership-application-form-2026-proof-v7
  const buildProofSlug = (proof) => {
    const slugify = (s) => (s || "")
      .toLowerCase()
      .replace(/['"`]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

    // Prefer the proof name (typically already descriptive: "8050 Bowood Hotel … Proof v7").
    // Only prepend the project name if the proof name doesn't already contain it.
    const base = proof.name || projectName || "proof";
    const baseSlug = slugify(base);
    const projectSlug = projectName ? slugify(projectName) : "";
    const withProject =
      proof.name && projectSlug && !baseSlug.includes(projectSlug)
        ? `${projectName} ${base}`
        : base;

    // Append vN only if not already present.
    const versionSuffix = proof.version ? `v${proof.version}` : "";
    const hasVersion = versionSuffix && new RegExp(`v${proof.version}\\b`, "i").test(withProject);
    const full = slugify(withProject + (versionSuffix && !hasVersion ? ` ${versionSuffix}` : ""));

    // Truncate to 80 chars while preserving the trailing -vN if present.
    if (full.length <= 80) return full;
    const trailingVersion = full.match(/-v\d+$/);
    if (trailingVersion) {
      const head = full.slice(0, 80 - trailingVersion[0].length).replace(/-+$/, "");
      return head + trailingVersion[0];
    }
    return full.slice(0, 80).replace(/-+$/, "");
  };

  const copyShareLink = async (proof) => {
    const slug = buildProofSlug(proof);
    const url = `${window.location.origin}/proof/${proof.share_token}${slug ? `/${slug}` : ""}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopiedId(proof.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  // Build a mailto: URL that opens the user's default mail client (Apple Mail
  // on Mac) with a pre-composed draft to send a proof link to the client(s).
  // `recipients` can be empty (open mail with no To), one contact, or many —
  // multiple addresses are comma-joined in the To field so a single draft
  // goes to everyone (rather than firing N separate emails).
  const buildProofMailto = (proof, recipients = []) => {
    const slug = buildProofSlug(proof);
    const url = `${window.location.origin}/proof/${proof.share_token}${slug ? `/${slug}` : ""}`;
    const list = (recipients || []).filter((r) => r?.email);
    // Greeting: "Hi <first name>" when exactly one recipient, "Hi all" for
    // two-plus, "Hi there" when no recipient is known.
    let greeting = "Hi there";
    if (list.length === 1) {
      greeting = `Hi ${list[0].name ? list[0].name.split(/\s+/)[0] : "there"}`;
    } else if (list.length > 1) {
      greeting = "Hi all";
    }
    const subject = `Proof ready for review — ${proof.name}${proof.version > 1 ? ` (v${proof.version})` : ""}`;
    const body = [
      `${greeting},`,
      "",
      `Your latest proof for ${projectName || "the project"} is ready to review. You can leave notes or sketch directly on the PDF, then approve or request changes when you're done — no login required.`,
      "",
      `View the proof: ${url}`,
      "",
      "Let me know if you have any questions.",
    ].join("\n");
    const to = list.length
      ? encodeURIComponent(list.map((r) => r.email).join(", "))
      : "";
    return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const openMail = (proof, recipients = []) => {
    // Fire-and-forget: log each recipient separately so the Email history
    // panel still shows individual people (rather than one lumped entry).
    const list = (recipients || []).filter((r) => r?.email || r?.name);
    const writes = list.length
      ? list.map((r) =>
          api.post(`/proofs/${proof.id}/email-log`, {
            recipient_name: r?.name || "",
            recipient_email: r?.email || "",
          })
        )
      : [
          api.post(`/proofs/${proof.id}/email-log`, { recipient_name: "", recipient_email: "" }),
        ];
    Promise.allSettled(writes).then(() => loadEmailLog(proof.id));
    window.location.href = buildProofMailto(proof, list);
  };

  // Per-proof email log state — { proofId: [entries] }, fetched lazily when
  // the user expands the "Email history" panel.
  const [emailLogs, setEmailLogs] = useState({});
  const [emailLogOpen, setEmailLogOpen] = useState({}); // { proofId: bool }
  const loadEmailLog = async (proofId) => {
    try {
      const { data } = await api.get(`/proofs/${proofId}`);
      setEmailLogs((prev) => ({ ...prev, [proofId]: data.email_log || [] }));
    } catch { /* ignore */ }
  };
  const toggleEmailLog = (proofId) => {
    const willOpen = !emailLogOpen[proofId];
    setEmailLogOpen((prev) => ({ ...prev, [proofId]: willOpen }));
    if (willOpen && !(proofId in emailLogs)) loadEmailLog(proofId);
  };

  const openViewer = async (proof) => {
    // Fetch the file as blob for inline viewing
    const res = await api.get(`/projects/${projectId}/proofs`); // refresh
    setProofs(res.data);
    const fresh = res.data.find((x) => x.id === proof.id) || proof;
    // Mark this proof as seen — clears its sidebar badge contribution.
    api.post(`/proofs/${fresh.id}/mark-seen`)
      .then(() => window.dispatchEvent(new CustomEvent("proofs:unseen-refresh")))
      .catch(() => {});
    const fileRes = await api.get(`/public/proofs/${fresh.share_token}/file`, { responseType: "blob" });
    const blob = new Blob([fileRes.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    setActiveFileBlobUrl(url);
    setActiveProof(fresh);
  };

  const closeViewer = () => {
    if (activeFileBlobUrl) window.URL.revokeObjectURL(activeFileBlobUrl);
    setActiveFileBlobUrl(null);
    setActiveProof(null);
  };

  const finishViewer = () => {
    closeViewer();
    if (fromParam === "proofing") {
      navigate("/proofing");
    }
  };


  const designerAddComment = async (page, x, y, text) => {
    const { data } = await api.post(`/proofs/${activeProof.id}/comments`, { page, x, y, text });
    setActiveProof((p) => ({ ...p, comments: [...(p.comments || []), data] }));
    return data;
  };

  const designerResolve = async (commentId, resolved) => {
    await api.patch(`/proofs/${activeProof.id}/comments/${commentId}`, { resolved });
    setActiveProof((p) => ({ ...p, comments: p.comments.map((c) => c.id === commentId ? { ...c, resolved } : c) }));
    // Refresh the sidebar's "unresolved proofs" badge — marking notes resolved
    // can flip this proof out of the orange-circle count.
    window.dispatchEvent(new CustomEvent("proofs:unseen-refresh"));
  };

  const designerAddAnnotation = async (page, points, color, text) => {
    const { data } = await api.post(`/proofs/${activeProof.id}/annotations`, { page, points, color, text });
    setActiveProof((p) => ({ ...p, annotations: [...(p.annotations || []), data] }));
    window.dispatchEvent(new CustomEvent("proofs:unseen-refresh"));
  };

  const designerDeleteAnnotation = async (id) => {
    await api.delete(`/proofs/${activeProof.id}/annotations/${id}`);
    setActiveProof((p) => ({ ...p, annotations: (p.annotations || []).filter((a) => a.id !== id) }));
    window.dispatchEvent(new CustomEvent("proofs:unseen-refresh"));
  };

  const designerDeleteComment = async (id) => {
    await api.delete(`/proofs/${activeProof.id}/comments/${id}`);
    setActiveProof((p) => ({ ...p, comments: (p.comments || []).filter((c) => c.id !== id) }));
    window.dispatchEvent(new CustomEvent("proofs:unseen-refresh"));
  };

  const designerResolveAnnotation = async (id, resolved) => {
    await api.patch(`/proofs/${activeProof.id}/annotations/${id}`, { resolved });
    setActiveProof((p) => ({ ...p, annotations: (p.annotations || []).map((a) => a.id === id ? { ...a, resolved } : a) }));
    window.dispatchEvent(new CustomEvent("proofs:unseen-refresh"));
  };

  // Designer reply on a client (or designer) note. Posts the reply to the
  // backend — which also fires an email to recipient_email if provided —
  // then merges the new reply into the active proof's local state so the
  // thread renders without a full reload.
  const designerAddReply = async (commentId, payload) => {
    const { data } = await api.post(`/proofs/${activeProof.id}/comments/${commentId}/replies`, payload);
    setActiveProof((p) => ({
      ...p,
      comments: (p.comments || []).map((c) =>
        c.id === commentId
          ? { ...c, replies: [...(c.replies || []), data] }
          : c
      ),
    }));
    return data;
  };


  // Group proofs by lineage root (parent_id || id)
  const byRoot = proofs.reduce((acc, p) => {
    const root = p.parent_id || p.id;
    acc[root] = acc[root] || [];
    acc[root].push(p);
    return acc;
  }, {});
  const allGroups = Object.entries(byRoot).map(([root, arr]) => {
    const sorted = arr.slice().sort((a, b) => b.version - a.version);
    return { root, current: sorted[0], history: sorted.slice(1) };
  }).sort((a, b) => new Date(b.current.created_at) - new Date(a.current.created_at));

  // Split into Active vs Completed (mirrors the global Proofing page rules):
  //   • completion_override = "completed" → forced Completed (manual relegation)
  //   • completion_override = "active"    → forced Active (manual revival)
  //   • otherwise auto-rule: status = "Pending Review" OR unresolved > 0 → Active.
  //     Approved or all-resolved Changes-Requested fall through to Completed.
  const groupBucket = (g) => {
    const cur = g.current;
    if (cur.completion_override === "completed") return "completed";
    if (cur.completion_override === "active") return "active";
    const status = (cur.status || "").toLowerCase();
    if (status === "approved") return "completed";
    const unresolved = (cur.comments || []).filter((c) => !c.resolved).length
                     + (cur.annotations || []).filter((a) => !a.resolved).length;
    if (status === "pending review" || unresolved > 0) return "active";
    return "completed";
  };
  const activeGroups = [];
  const completedGroups = [];
  allGroups.forEach((g) => {
    if (groupBucket(g) === "active") activeGroups.push(g);
    else completedGroups.push(g);
  });

  // Toggle the bucket for a single proof (the lineage's current head). Optimistic.
  const toggleProofCompletion = async (proof, targetState) => {
    setProofs((prev) => prev.map((x) => x.id === proof.id ? { ...x, completion_override: targetState } : x));
    try {
      await api.post(`/proofs/${proof.id}/completion`, { state: targetState });
      toast.success(targetState === "completed" ? "Moved to Completed Proofs" : "Moved back to Active");
    } catch (err) {
      // Roll back on failure.
      setProofs((prev) => prev.map((x) => x.id === proof.id ? { ...x, completion_override: proof.completion_override ?? null } : x));
      toast.error("Couldn't move proof", { description: err?.response?.data?.detail || err?.message });
    }
  };



  // ----- Per-lineage group renderer (returns the big card for one
  // proof family). Declared as a `function` (not const) so the JSX above
  // can call it before this line; function declarations are hoisted
  // to the top of the enclosing function scope. -----
  function renderProofGroup({ root, current, history }, bucket = "active") {
    const totalNotes = (current.comments || []).length;
    const totalDrawings = (current.annotations || []).length;
    const unresolvedNotes = (current.comments || []).filter((c) => !c.resolved).length;
    const unresolvedDrawings = (current.annotations || []).filter((a) => !a.resolved).length;
    const hasUnresolved = unresolvedNotes + unresolvedDrawings > 0;
    const hadFeedback = totalNotes + totalDrawings > 0;
    const isApproved = current.status === "Approved";
    const isChangesRequested = current.status === "Changes Requested";
    // Special: changes were requested AND all have been resolved
    const changesComplete = isChangesRequested && hadFeedback && !hasUnresolved;

    const wrapperTint = changesComplete
      ? "bg-sky-50/80 border-sky-300"
      : isApproved
        ? "bg-emerald-50/80 border-emerald-300"
        : isChangesRequested || hasUnresolved
          ? "bg-rose-50/80 border-rose-300"
          : "border-zinc-200";

    const displayStatus = changesComplete ? "Requested changes complete" : current.status;
    const displayStatusClasses = changesComplete
      ? "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border bg-sky-100 text-sky-800 border-sky-300"
      : null;

    return (
    <div key={root} data-testid={`proof-group-${current.id}`} className={`border-2 rounded-xl overflow-hidden transition-colors ${wrapperTint}`}>
      <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <FileText className="w-5 h-5 text-zinc-400 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-zinc-950 truncate">{current.name} <span className="text-zinc-400 font-medium">v{current.version}</span></p>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              Uploaded {formatDateUK(current.created_at)} · {current.comments?.length || 0} note{(current.comments?.length || 0) !== 1 ? "s" : ""}
              {history.length > 0 && ` · ${history.length} previous version${history.length > 1 ? "s" : ""}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {displayStatusClasses ? (
            <span className={displayStatusClasses} data-testid={`proof-status-changes-complete-${current.id}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-sky-500" />
              {displayStatus}
            </span>
          ) : (
            <StatusBadge status={current.status} />
          )}
          <button
            onClick={() => copyShareLink(current)}
            className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full transition-all ${copiedId === current.id ? "bg-emerald-500 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}
            data-testid={`proof-copy-link-${current.id}`}
          >
            {copiedId === current.id ? <><Check className="w-3 h-3" />Copied</> : <><LinkIcon className="w-3 h-3" />Copy share link</>}
          </button>
          {contactEmails.length <= 1 ? (
            <button
              type="button"
              onClick={() => openMail(current, contactEmails.length ? [contactEmails[0]] : [])}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-zinc-100 text-zinc-700 hover:bg-zinc-200 transition-colors"
              data-testid={`proof-email-link-${current.id}`}
              title={contactEmails[0]?.email ? `Open new email to ${contactEmails[0].email}` : "Open new email (pick recipient in Mail)"}
            >
              <Mail className="w-3 h-3" />Email link
            </button>
          ) : (
            <RecipientMultiSelectPopover
              contacts={contactEmails}
              onSend={(selected) => openMail(current, selected)}
              triggerTestId={`proof-email-link-${current.id}`}
              listTestIdPrefix={`proof-email-recipients-${current.id}`}
            />
          )}
          <Btn size="sm" variant="secondary" onClick={() => openViewer(current)} data-testid={`proof-open-${current.id}`}>
            <Eye className="w-3.5 h-3.5" />Open
          </Btn>
          <input
            type="file"
            accept="application/pdf"
            ref={(r) => { if (versionFor === current.id) versionFileRef.current = r; }}
            onChange={(e) => onVersionFile(current.id, e)}
            className="hidden"
            data-testid={`proof-version-input-${current.id}`}
          />
          <Btn
            size="sm"
            variant="secondary"
            onClick={() => {
              setVersionFor(current.id);
              setTimeout(() => versionFileRef.current?.click(), 50);
            }}
            data-testid={`proof-new-version-${current.id}`}
          ><Upload className="w-3.5 h-3.5" />New version</Btn>
          <button
            type="button"
            onClick={() => toggleProofCompletion(current, bucket === "completed" ? "active" : "completed")}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-zinc-100 text-zinc-700 hover:bg-zinc-200 transition-colors"
            title={bucket === "completed" ? "Move this proof back to the Active list" : "Move this proof to the Completed Proofs panel"}
            data-testid={`proof-toggle-completion-${current.id}`}
          >
            {bucket === "completed"
              ? <><RotateCcw className="w-3 h-3" />Move to Active</>
              : <><Archive className="w-3 h-3" />Move to Completed</>}
          </button>
          {confirmDeleteId === current.id ? (
            <div className="inline-flex items-center gap-1 bg-red-50 border border-red-200 rounded-full pl-3 pr-1 py-1" data-testid={`proof-delete-confirm-${current.id}`}>
              <span className="text-xs font-semibold text-red-700">Delete this proof?</span>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); del(current.id); }}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-red-500 text-white hover:bg-red-600"
                data-testid={`proof-delete-confirm-yes-${current.id}`}
              >Yes, delete</button>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmDeleteId(null); }}
                className="px-2 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-100 rounded-full"
                data-testid={`proof-delete-confirm-no-${current.id}`}
              >Cancel</button>
            </div>
          ) : (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmDeleteId(current.id); }}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-xs font-semibold bg-red-500 text-white hover:bg-red-600 shadow-sm"
              data-testid={`proof-delete-${current.id}`}
              title="Delete this proof and all its comments"
            >
              <Trash2 className="w-3.5 h-3.5" />Delete
            </button>
          )}
        </div>
      </div>
      {/* Email history — collapsible audit trail of who got this proof's link */}
      <div className="border-t border-zinc-100 bg-white" data-testid={`proof-email-history-${current.id}`}>
        <button
          type="button"
          onClick={() => toggleEmailLog(current.id)}
          className="w-full flex items-center justify-between px-4 py-2 hover:bg-zinc-50 transition-colors"
          data-testid={`proof-email-history-toggle-${current.id}`}
        >
          <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1.5">
            <Clock className="w-3 h-3" />
            Email history
            {emailLogs[current.id]?.length > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-zinc-200 text-zinc-700 text-[10px] font-bold normal-case tracking-normal">
                {emailLogs[current.id].length}
              </span>
            )}
          </span>
          {emailLogOpen[current.id] ? <ChevronUp className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
        </button>
        {emailLogOpen[current.id] && (
          <div className="px-4 pb-3 -mt-1" data-testid={`proof-email-history-list-${current.id}`}>
            {!(current.id in emailLogs) ? (
              <p className="text-xs text-zinc-400 py-2">Loading…</p>
            ) : (emailLogs[current.id] || []).length === 0 ? (
              <p className="text-xs text-zinc-500 italic py-2">No emails sent yet. Click <strong>Email link</strong> above to open a draft for a contact — it'll be logged here.</p>
            ) : (
              <ul className="space-y-1.5">
                {[...(emailLogs[current.id] || [])].reverse().map((entry, idx) => (
                  <li key={idx} className="flex items-center justify-between gap-3 text-xs bg-zinc-50 rounded-md px-3 py-1.5 border border-zinc-100" data-testid={`proof-email-history-entry-${current.id}-${idx}`}>
                    <div className="min-w-0 flex-1">
                      <span className="font-semibold text-zinc-900">{entry.recipient_name || "(unknown)"}</span>
                      {entry.recipient_email && (
                        <span className="text-zinc-500 ml-1.5">&lt;{entry.recipient_email}&gt;</span>
                      )}
                    </div>
                    <span className="text-zinc-500 tabular-nums shrink-0">
                      {new Date(entry.sent_at).toLocaleString("en-GB")}
                    </span>
                  </li>
                ))}
                <li className="text-[10px] text-zinc-400 pt-1">
                  Logged when the email draft was opened — mailto can't confirm delivery, so treat as "draft prepared", not "sent &amp; received".
                </li>
              </ul>
            )}
          </div>
        )}
      </div>
      {history.length > 0 && (
        <div className="border-t border-zinc-100 bg-zinc-50/60 px-4 py-2">
          <p className="text-[11px] text-zinc-500 font-semibold uppercase tracking-wider mb-1">Previous versions</p>
          <div className="flex flex-wrap gap-2">
            {history.map((h) => (
              <button
                key={h.id}
                onClick={() => openViewer(h)}
                className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 bg-white border border-zinc-200 rounded-full text-zinc-700 hover:bg-zinc-100"
                data-testid={`proof-history-${h.id}`}
              >
                v{h.version} · {formatDateUK(h.created_at)} <span className="text-zinc-400">({h.status})</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
    );
  }

  return (
    <Card className="p-6 border-2 border-purple-200 bg-gradient-to-br from-purple-50/50 to-white shadow-sm" data-testid="project-proofs-section">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-100 grid place-items-center shrink-0">
            <FileText className="w-5 h-5 text-purple-700" />
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-widest text-purple-700 font-bold">Online Proofing</p>
            <h2 className="font-display text-xl font-bold text-zinc-950">Client Proofs</h2>
            <p className="text-xs text-zinc-600 mt-0.5">Upload PDF designs, share a link with your client, and gather feedback right on the page.</p>
          </div>
        </div>
        <Btn size="sm" onClick={() => setShowUploadForm((v) => !v)} data-testid="proof-upload-toggle">
          <Plus className="w-3.5 h-3.5" />New Proof
        </Btn>
      </div>

      <div className="border-t border-purple-200/70 my-4" />

      {showUploadForm && (
        <form onSubmit={submitNew} className="mb-4 p-4 bg-zinc-50 border border-zinc-200 rounded-xl space-y-3 animate-fade-in" data-testid="proof-upload-form">
          <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <Label>Proof name</Label>
              <Input
                required
                placeholder={`e.g. ${projectName} – Logo concepts v1`}
                value={name}
                onChange={(e) => { setName(e.target.value); setNameTouched(true); }}
                data-testid="proof-name-input"
              />
            </div>
            <div>
              <Label>PDF file</Label>
              <input
                type="file"
                accept="application/pdf"
                required
                onChange={(e) => {
                  const f = e.target.files[0];
                  setFile(f);
                  if (f && !nameTouched) {
                    // strip .pdf extension and use filename as default proof name
                    const baseName = f.name.replace(/\.pdf$/i, "");
                    setName(baseName);
                  }
                }}
                data-testid="proof-file-input"
                className="block text-sm"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Btn type="button" variant="secondary" size="sm" onClick={() => { setShowUploadForm(false); setName(""); setNameTouched(false); setFile(null); }}>Cancel</Btn>
            <Btn type="submit" size="sm" disabled={uploading} data-testid="proof-upload-submit">{uploading ? "Uploading…" : "Upload proof"}</Btn>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : allGroups.length === 0 ? (
        <p className="text-sm text-zinc-500 text-center py-6">No proofs uploaded yet.</p>
      ) : (
        <div className="space-y-3">
          {activeGroups.length === 0 && (
            <p className="text-xs text-zinc-500 italic px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-lg" data-testid="project-proofs-no-active">
              No active proofs — every proof here is complete. Upload a new version above to start the next round.
            </p>
          )}
          {activeGroups.map((g) => renderProofGroup(g, "active"))}

          {completedGroups.length > 0 && (
            <div className="border-2 border-dashed border-zinc-200 rounded-xl bg-zinc-50/40" data-testid="project-proofs-completed-section">
              <button
                type="button"
                onClick={() => setCompletedOpen((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-100/60 transition-colors rounded-xl"
                data-testid="project-proofs-completed-toggle"
              >
                <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600 inline-flex items-center gap-2">
                  <Check className="w-3.5 h-3.5 text-emerald-600" />
                  Completed proofs
                  <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-zinc-200 text-zinc-700 text-[10px] font-bold normal-case tracking-normal">
                    {completedGroups.length}
                  </span>
                </span>
                {completedOpen
                  ? <ChevronUp className="w-4 h-4 text-zinc-500" />
                  : <ChevronDown className="w-4 h-4 text-zinc-500" />}
              </button>
              {completedOpen && (
                <div className="border-t border-zinc-200 p-3 space-y-3" data-testid="project-proofs-completed-list">
                  {completedGroups.map((g) => renderProofGroup(g, "completed"))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Inline viewer modal */}
      {activeProof && activeFileBlobUrl && (
        <div className="fixed inset-0 z-50 flex flex-col bg-zinc-50 animate-fade-in" data-testid="proof-viewer-modal">
          <div className="bg-white border-b border-zinc-200 px-4 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-zinc-950 truncate">{activeProof.name} <span className="text-zinc-400 font-medium">v{activeProof.version}</span></p>
              <p className="text-[11px] text-zinc-500">{activeProof.comments?.length || 0} notes · share link: <code className="bg-zinc-100 rounded px-1.5 py-0.5 text-[10px]">/proof/{activeProof.share_token}</code></p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => copyShareLink(activeProof)}
                className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full ${copiedId === activeProof.id ? "bg-emerald-500 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}
                data-testid="proof-viewer-copy-link"
              >
                {copiedId === activeProof.id ? <><Check className="w-3 h-3" />Copied</> : <><Copy className="w-3 h-3" />Copy share link</>}
              </button>
              {contactEmails.length <= 1 ? (
                <button
                  type="button"
                  onClick={() => openMail(activeProof, contactEmails.length ? [contactEmails[0]] : [])}
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                  data-testid="proof-viewer-email-link"
                  title={contactEmails[0]?.email ? `Open new email to ${contactEmails[0].email}` : "Open new email (pick recipient in Mail)"}
                >
                  <Mail className="w-3 h-3" />Email link
                </button>
              ) : (
                <RecipientMultiSelectPopover
                  contacts={contactEmails}
                  onSend={(selected) => openMail(activeProof, selected)}
                  triggerTestId="proof-viewer-email-link"
                  listTestIdPrefix="proof-viewer-email-recipients"
                />
              )}
              <button
                type="button"
                onClick={finishViewer}
                className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm"
                data-testid="proof-viewer-done"
              >
                <Check className="w-4 h-4" />
                {fromParam === "proofing" ? "Done — back to Proofing" : "Done"}
              </button>
            </div>
          </div>
          <div className="flex-1 p-4 overflow-hidden">
            <PdfProofViewer
              fileUrl={activeFileBlobUrl}
              comments={activeProof.comments || []}
              annotations={activeProof.annotations || []}
              pageNotes={activeProof.page_notes || {}}
              onAddComment={designerAddComment}
              onResolveComment={designerResolve}
              onDeleteComment={designerDeleteComment}
              onAddAnnotation={designerAddAnnotation}
              onDeleteAnnotation={designerDeleteAnnotation}
              onResolveAnnotation={designerResolveAnnotation}
              onAddReply={designerAddReply}
              replyRole="designer"
              replyContacts={contactEmails}
              attachmentBaseUrl={`${API}/public/proofs/${activeProof.share_token}/attachments`}
              canEdit={!activeProof.archived}
              canDelete={() => true}
              canDeleteAnnotation={() => true}
              authorName="Designer"
            />
          </div>
        </div>
      )}
    </Card>
  );
};

export default ProjectProofs;
